import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV, LOCATIONS, SERVICES, IMAGES } from "../data/site";
import { useHeroContext } from "../lib/HeroContext";
// https://calendly.com/mywebflowio/virtual
const CALENDLY_URL = "https://calendly.com/mywebflowio/virtual";

const NavDropdown = ({ label, items, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div
      className="relative"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-dropdown-${testIdPrefix}`}
        className="flex items-center gap-1.5 text-[11px] tracking-[0.28em] uppercase font-medium text-ink hover:text-gold transition-colors duration-300"
      >
        {label}
        <ChevronDown
          size={12}
          strokeWidth={1.4}
          className={`transition-transform duration-300 ${open ? "rotate-180 text-gold" : ""}`}
        />
      </button>
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-300 ${
          open
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible -translate-y-2"
        }`}
      >
        <div
          className="bg-cream border border-pearl shadow-xl min-w-[260px] py-3"
          data-testid={`nav-dropdown-${testIdPrefix}-menu`}
        >
          {items.map((it) => (
            <React.Fragment key={it.href}>
              {it.divider && (
                <div className="my-2 border-t border-pearl mx-6" />
              )}
              <Link
                to={it.href}
                data-testid={`nav-dropdown-${testIdPrefix}-${it.slug}`}
                onClick={() => setOpen(false)}
                className="block px-6 py-3 font-serif text-base text-ink hover:text-gold hover:bg-pearl/40 transition-colors"
              >
                {it.label}
                {it.sub && (
                  <span className="block text-[10px] tracking-[0.3em] uppercase text-ink/40 mt-0.5">
                    {it.sub}
                  </span>
                )}
              </Link>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const { hoveredPane, heroColor } = useHeroContext();
  const [scrolled, setScrolled] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const openCalendlyPopup = () => {
    const popupUrl = `${CALENDLY_URL}?hide_gdpr_banner=1`;
    const initPopup = () => {
      if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
        window.Calendly.initPopupWidget({ url: popupUrl });
        return true;
      }
      return false;
    };

    if (initPopup()) {
      return;
    }

    const styleId = "calendly-widget-styles";
    if (!document.getElementById(styleId)) {
      const calendlyStyles = document.createElement("link");
      calendlyStyles.id = styleId;
      calendlyStyles.rel = "stylesheet";
      calendlyStyles.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(calendlyStyles);
    }

    const scriptId = "calendly-widget-script";
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.addEventListener("load", initPopup, { once: true });
      return;
    }

    const calendlyScript = document.createElement("script");
    calendlyScript.id = scriptId;
    calendlyScript.src = "https://assets.calendly.com/assets/external/widget.js";
    calendlyScript.async = true;
    calendlyScript.onload = () => {
      if (!initPopup()) {
        window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
      }
    };
    calendlyScript.onerror = () => {
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    };
    document.body.appendChild(calendlyScript);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
  }, [location.pathname]);

  const servicesItems = [
    ...SERVICES.map((s) => ({
      slug: s.slug,
      label: s.name,
      href: `/services/${s.slug}`,
      sub: s.category,
    })),
  ];
  const locationsItems = LOCATIONS.map((l) => ({
    slug: l.slug,
    label: l.name,
    href: `/locations/${l.slug}`,
    sub: l.city,
  }));

  const isActive = hoveredPane !== null;
  const anyActive = hoveredPane !== null;
  const dimmed = anyActive && !isActive;
  const showLogoImage = isHomePage || scrolled || (!isHomePage && isHeaderHovered);
  const allowLogoShine = showLogoImage;
        // scrolled ? isActive ? "bg-[#6B6F77] " : dimmed ? "bg-[#6B6F77] bg-ink/40" : "bg-[#6B6F77]" : "bg-transparent"

  return (
    <header
      data-testid="site-header"
      onMouseEnter={() => setIsHeaderHovered(true)}
      onMouseLeave={() => setIsHeaderHovered(false)}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-700 ${
        scrolled ?  isActive ? "bg-gradient-to-b from-ink/50 via-ink/50 to-ink/50" : "bg-gradient-to-b from-ink/30 via-ink/30 to-ink/30 " : "bg-transparent"
      }`}
      style={{ backgroundColor: scrolled ? heroColor : "transparent" }}
    >
      <style>{`
        @keyframes logoShine {
          0%   { -webkit-mask-position: 0%;   mask-position: 0%;   }
          100% { -webkit-mask-position: 120%; mask-position: 120%; }
        }
        .logo-shine {
          -webkit-mask-image: linear-gradient(45deg,#000 25%,rgba(0,0,0,.2) 50%,#000 75%);
          mask-image: linear-gradient(45deg,#000 25%,rgba(0,0,0,.2) 50%,#000 75%);
          -webkit-mask-size: 800%;
          mask-size: 800%;
          -webkit-mask-position: 0;
          mask-position: 0;
        }
        .logo-shine.shining {
          animation: logoShine 1.5s ease infinite;
        }
      `}</style>
      <div className="container-wide grid grid-cols-[1fr_auto_1fr] items-center py-5 md:py-6">
        {/* Left col — book button + 20% gap + NJ phone */}
        <div className="flex items-center justify-between ">
          <button
            type="button"
            data-testid="header-book-btn"
            onClick={openCalendlyPopup}
            className={`hidden sm:inline-flex  btn-primary text-xs py-2.5 px-3 transition-all duration-500 ${scrolled ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
          >
            Book a Consult
          </button>
          <div className="flex flex-col items-center ">
            <a
              href="https://gdentalspa.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden sm:block transition-all duration-500 hover:text-gold text-ink text-center ${scrolled ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            >
              <span className="block text-xs text-[#00ff1a] tracking-wide ">
                gdental spa
              </span>
            </a>
            <a
              href={`tel:${LOCATIONS[0].phone.replace(/\D/g, "")}`}
              className={`hidden sm:block transition-all duration-500 hover:text-gold text-white text-center ${scrolled ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            >
              <span className="text-xs tracking-wide ">
                {LOCATIONS[0].phone}
              </span>
            </a>
          </div>
          <div className="w-[0%]" />

        </div>
        {/* Center col — logo + brand words */}
        <Link
          to="/"
          onClick={() => window.scrollTo(0, 0)}
          onMouseEnter={() => allowLogoShine && setIsLogoHovered(true)}
          onMouseLeave={() => setIsLogoHovered(false)}
          data-testid="header-logo-link"
          className="flex flex-col items-center justify-center w-full"
        >
          <div className="flex items-center justify-center gap-[10px] w-full">
            <span
              className={`font-script text-4xl sm:text-6xl md:text-5xl  text-[#F58220] transition-all duration-500 ${
                scrolled ? "opacity-100" : "opacity-0"
              }`}
            >
              Smiles
            </span>
            <div className="relative">
              {showLogoImage && (
                <img
                  src={IMAGES.logo}
                  alt="Smiles by Dr. K Logo"
                  className={`h-12 w-12 object-contain transition-opacity duration-700 ${isLogoHovered && allowLogoShine ? "logo-shine shining" : ""} opacity-100`}
                />
              )}
            </div>
            <span
              className={`font-script text-4xl sm:text-6xl md:text-5xl  text-[#F58220] transition-all duration-500 ${
                scrolled ? "opacity-100" : "opacity-0"
              }`}
            >
              by Dr.K
            </span>
          </div>
        </Link>

        {/* Right col — NY phone + 20% gap + button */}
        <div className="flex items-center  justify-between ">
          <div className="w-[0%]" />

          <div className="flex flex-col items-center pr-20">
            <a
              href="https://edentalspa.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden sm:block transition-all duration-500 hover:text-gold text-ink text-center ${scrolled ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            >
              <span className="block text-xs tracking-wide text-[#00FFFF]">
                edental spa
              </span>
            </a>
            <a
              href={`tel:${LOCATIONS[1].phone.replace(/\D/g, "")}`}
              className={`hidden sm:block transition-all duration-500 hover:text-gold text-white text-center ${scrolled ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            >
              <span className="text-xs tracking-wide ">
                {LOCATIONS[1].phone}
              </span>
            </a>
          </div>

          <button
            onClick={() => setServicesOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className={`transition-all duration-500 hover:text-gold ${scrolled ? "text-white opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
          >
            <Menu size={24} strokeWidth={1.4} />
          </button>
          {/* Mobile: phone icon */}
          <a
            href={`tel:${LOCATIONS[0].phone.replace(/\D/g, "")}`}
            className={`sm:hidden transition-all duration-500 hover:text-gold ${scrolled ? "text-ink opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            aria-label="Call us"
          >
            <Phone size={20} strokeWidth={1.4} />
          </a>
        </div>
         {/* <div
          className={`pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/20 via-ink/20 to-ink/45 transition-opacity duration-500 ${
            scrolled ? "opacity-100" : "opacity-0"
          }`}
        /> */}
        {/*}
        <div
          className={`pointer-events-none absolute inset-0 bg-gradient-to-r from-ink/20 via-transparent to-ink/20 transition-opacity duration-500 ${
            scrolled ? "opacity-100" : "opacity-0"
          }`}
        /> */}
      </div>

      {/* Services Sidebar - slides from right */}
      <div
        className={`fixed top-0 right-0 h-screen w-80 bg-bone shadow-2xl z-40 transition-transform duration-500 ${
          servicesOpen ? "translate-x-0" : "translate-x-full"
        } overflow-y-auto`}
        data-testid="services-sidebar"
      >
        <div className="p-8">
          <div className="flex items-center justify-end mb-8">
            <button
              onClick={() => setServicesOpen(false)}
              data-testid="services-sidebar-close"
              className="text-ink hover:text-gold transition-colors"
            >
              <X size={24} strokeWidth={1.4} />
            </button>
          </div>
          <div className="flex flex-col gap-6">
            {/* About Link */}
            <Link
              to="/about"
              onClick={() => setServicesOpen(false)}
              data-testid="sidebar-about"
              className="font-serif text-lg text-ink hover:text-gold transition-colors"
            >
              About
            </Link>
            {/* smilegaller */}
            <Link
              to="/gallery"
              onClick={() => setServicesOpen(false)}
              data-testid="sidebar-gallery"
              className="font-serif text-lg text-ink hover:text-gold transition-colors"
            >
              SmileGallery
            </Link>

            {/* Services */}
            <div>
              <button
                onClick={() => setServicesExpanded((o) => !o)}
                className="w-full flex items-center justify-between font-serif text-lg text-ink hover:text-gold transition-colors"
              >
                Services
                <ChevronDown
                  size={16}
                  strokeWidth={1.4}
                  className={`transition-transform duration-300 ${servicesExpanded ? "rotate-180 text-gold" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-[max-height] duration-500 ${servicesExpanded ? "max-h-[600px]" : "max-h-0"}`}
              >
                <div className="flex flex-col gap-1 ml-1 mt-3">
                  {servicesItems.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      onClick={() => setServicesOpen(false)}
                      data-testid={`sidebar-service-${service.slug}`}
                      className="text-sm text-ink hover:text-gold transition-colors py-1.5 border-b border-ink/5 last:border-0"
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Locations */}
            <div>
              <button
                onClick={() => setLocationsExpanded((o) => !o)}
                className="w-full flex items-center justify-between font-serif text-lg text-ink hover:text-gold transition-colors"
              >
                Locations
                <ChevronDown
                  size={16}
                  strokeWidth={1.4}
                  className={`transition-transform duration-300 ${locationsExpanded ? "rotate-180 text-gold" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-[max-height] duration-500 ${locationsExpanded ? "max-h-[200px]" : "max-h-0"}`}
              >
                <div className="flex flex-col gap-1 ml-1 mt-3">
                  {locationsItems.map((loc) => (
                    <Link
                      key={loc.href}
                      to={loc.href}
                      onClick={() => setServicesOpen(false)}
                      data-testid={`sidebar-location-${loc.slug}`}
                      className="py-1.5 border-b border-ink/5 last:border-0"
                    >
                      <div className="text-sm text-ink hover:text-gold transition-colors">
                        {loc.label}
                      </div>
                      <div className="text-xs tracking-[0.3em] uppercase text-ink/40">
                        {loc.sub}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Reviews Link */}
            <Link
              to="/reviews"
              onClick={() => setServicesOpen(false)}
              data-testid="sidebar-reviews"
              className="font-serif text-lg text-ink hover:text-gold transition-colors"
            >
              What Patients Say
            </Link>

            {/* Contact Link */}
            <Link
              to="/contact"
              onClick={() => setServicesOpen(false)}
              data-testid="sidebar-contact"
              className="font-serif text-lg text-ink hover:text-gold transition-colors"
            >
              Contact
            </Link>

            {/* Phone */}
            {/* <a
              href={`tel:${LOCATIONS[0].phone.replace(/\D/g, "")}`}
              data-testid="sidebar-phone"
              className="flex items-center gap-2 text-sm text-ink hover:text-gold transition-colors mt-4 pt-4 border-t border-ink/10"
            >
              <Phone size={14} strokeWidth={1.2} />
              {LOCATIONS[0].phone}
            </a> */}

            {/* Book Button */}
            <Link
              to="/contact"
              data-testid="sidebar-book-btn"
              className="btn-primary mt-6 text-center"
              onClick={() => setServicesOpen(false)}
            >
              Book a Consult
            </Link>
          </div>
        </div>
      </div>

      {/* Services Sidebar Overlay */}
      {servicesOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30"
          onClick={() => setServicesOpen(false)}
          data-testid="services-sidebar-overlay"
        />
      )}
    </header>
  );
};
